/**
 * Phase 1.5 Milestone D — canonical status contract for the import wizard.
 *
 * The 11 statuses below are the exact `IMPORT_STATES` enum from
 * `backend/app/models/import_domain.py` (RECEIVED, ANALYZING, MAPPED,
 * VALIDATING, VALIDATED, COMMITTING, CANCELLING, COMPLETED, PARTIAL, FAILED,
 * CANCELLED). Never invent statuses. Any string coming back from the API that
 * is NOT one of these gets routed to the safe recovery screen instead of
 * breaking the wizard.
 *
 * `wizardStepFor(status)` derives which of the 5 wizard steps to render.
 * Progress/summary rendering nuances (loading vs interactive vs cancelling
 * banner vs terminal card) are decided by the step components using the
 * `status` string directly — this file only maps status -> step number.
 */

export const IMPORT_STATUSES = [
  "RECEIVED",
  "ANALYZING",
  "MAPPED",
  "VALIDATING",
  "VALIDATED",
  "COMMITTING",
  "CANCELLING",
  "COMPLETED",
  "PARTIAL",
  "FAILED",
  "CANCELLED",
] as const;

export type ImportStatus = (typeof IMPORT_STATUSES)[number];

export const TERMINAL_STATUSES: ImportStatus[] = [
  "COMPLETED",
  "PARTIAL",
  "FAILED",
  "CANCELLED",
];

export function isKnownImportStatus(v: unknown): v is ImportStatus {
  return typeof v === "string" && (IMPORT_STATUSES as readonly string[]).includes(v);
}

export function isTerminalStatus(v: unknown): boolean {
  return isKnownImportStatus(v) && TERMINAL_STATUSES.includes(v);
}

export type WizardStep = 1 | 2 | 3 | 4 | 5;

/**
 * Map a real backend status to the wizard step to render. Unknown statuses
 * return `null` so the shell can render the safe recovery screen.
 *
 * Note: after POST /upload the job stays in RECEIVED — there is no UPLOADED
 * or ANALYZED state in the backend enum. Analyze transitions RECEIVED -> ANALYZING -> MAPPED.
 */
export function wizardStepFor(status: unknown): WizardStep | null {
  if (!isKnownImportStatus(status)) return null;
  switch (status) {
    case "RECEIVED":
      return 2; // Upload (or analyze CTA after a file is attached)
    case "ANALYZING":
    case "MAPPED":
      return 3; // Mapping
    case "VALIDATING":
    case "VALIDATED":
      return 4; // Review and resolve
    case "COMMITTING":
    case "CANCELLING":
    case "COMPLETED":
    case "PARTIAL":
    case "FAILED":
    case "CANCELLED":
      return 5; // Commit and outcomes
  }
}

/** Human-readable step title for the stepper header. */
export const STEP_TITLES: Record<WizardStep, string> = {
  1: "Setup",
  2: "Upload",
  3: "Map columns",
  4: "Review & resolve",
  5: "Commit & track",
};

/** For the job list — user-facing pill label per status. */
export const STATUS_LABELS: Record<ImportStatus, string> = {
  RECEIVED: "Received",
  ANALYZING: "Analyzing",
  MAPPED: "Mapped",
  VALIDATING: "Validating",
  VALIDATED: "Validated",
  COMMITTING: "Committing",
  CANCELLING: "Cancelling",
  COMPLETED: "Completed",
  PARTIAL: "Partial",
  FAILED: "Failed",
  CANCELLED: "Cancelled",
};

/** Tailwind pill classes per status; safe fallback for unknowns. */
export function statusPillClass(status: unknown): string {
  const map: Record<ImportStatus, string> = {
    RECEIVED: "bg-slate-100 text-slate-700 border-slate-200",
    ANALYZING: "bg-blue-50 text-blue-700 border-blue-200",
    MAPPED: "bg-indigo-50 text-indigo-700 border-indigo-200",
    VALIDATING: "bg-violet-50 text-violet-700 border-violet-200",
    VALIDATED: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
    COMMITTING: "bg-amber-50 text-amber-800 border-amber-200",
    CANCELLING: "bg-orange-50 text-orange-800 border-orange-200",
    COMPLETED: "bg-emerald-50 text-emerald-800 border-emerald-200",
    PARTIAL: "bg-yellow-50 text-yellow-800 border-yellow-200",
    FAILED: "bg-rose-50 text-rose-800 border-rose-200",
    CANCELLED: "bg-zinc-100 text-zinc-700 border-zinc-200",
  };
  return isKnownImportStatus(status)
    ? map[status]
    : "bg-zinc-100 text-zinc-500 border-zinc-200";
}
